import type { BrightnessId, MutagenId, NatalDecorativeCycle, NormalizedChart, PalaceId } from './domain.js';

export interface ProductionChartStarViewModel {
  id: string;
  label: string;
  category: 'major' | 'minor' | 'adjective' | 'cycle' | 'other';
  brightness: BrightnessId;
  brightnessLabel: string;
  birthYearMutagens: readonly MutagenId[];
}

export interface ProductionChartDecorativeStarViewModel {
  id: string;
  label: string;
  cycle: NatalDecorativeCycle;
  cycleLabel: string;
}

export interface ProductionChartRelationNavigationViewModel {
  palaceId: PalaceId;
  palaceLabel: string;
  relation: 'opposite' | 'trine' | 'adjacent' | 'derived' | 'same';
}

export interface ProductionChartPalaceViewModel {
  palaceId: PalaceId;
  palaceLabel: string;
  heavenlyStem: string;
  earthlyBranch: string;
  isSoulPalace: boolean;
  isBodyPalace: boolean;
  stars: readonly ProductionChartStarViewModel[];
  decorativeStars: readonly ProductionChartDecorativeStarViewModel[];
  relations: readonly ProductionChartRelationNavigationViewModel[];
}
export interface ProductionChartStarCoverageViewModel {
  corePlacements: number;
  decorativePlacements: number;
  totalPlacements: number;
  uniqueStarLabels: number;
  expectedUniqueStarLabels: 108;
  complete108: boolean;
  disclosure: string;
}

export interface ProductionChartCenterViewModel {
  zodiac: string;
  fiveElementsClass: string;
  soulStarLabel: string;
  bodyStarLabel: string;
}

export interface ProductionChartPageViewModel {
  model: 'PRODUCTION-CHART-PAGE-0.3.0';
  palaceOrder: readonly PalaceId[];
  palaces: readonly ProductionChartPalaceViewModel[];
  desktopLayout: 'twelve_palace_grid';
  mobileLayout: 'focus_palace';
  defaultFocusPalaceId: PalaceId;
  center: ProductionChartCenterViewModel;
  starCoverage: ProductionChartStarCoverageViewModel;
  disclosure: string;
}

const CYCLE_LABELS: Readonly<Record<NatalDecorativeCycle, string>> = Object.freeze({
  changsheng12: '長生十二神',
  boshi12: '博士十二神',
  jiangqian12: '將前十二神',
  suiqian12: '歲前十二神',
});
function chartStars(chart: NormalizedChart, palaceId: PalaceId): readonly ProductionChartStarViewModel[] {
  const palace = chart.palaces[palaceId];
  return Object.freeze([...palace.majorStars, ...palace.minorStars, ...palace.adjectiveStars]
    .map((star) => Object.freeze({
      id: star.id,
      label: star.label,
      category: star.category,
      brightness: star.brightness,
      brightnessLabel: star.brightnessSourceLabel,
      birthYearMutagens: Object.freeze([...star.mutagens]),
    }))
    .sort((a, b) => a.category.localeCompare(b.category) || a.id.localeCompare(b.id)));
}

function chartDecorativeStars(chart: NormalizedChart, palaceId: PalaceId): readonly ProductionChartDecorativeStarViewModel[] {
  const stars = chart.palaces[palaceId].decorativeStars ?? [];
  return Object.freeze(stars.map((star) => Object.freeze({
    id: star.id,
    label: star.label,
    cycle: star.cycle,
    cycleLabel: CYCLE_LABELS[star.cycle],
  })));
}

function buildStarCoverage(palaces: readonly ProductionChartPalaceViewModel[]): ProductionChartStarCoverageViewModel {
  const corePlacements = palaces.reduce((sum, palace) => sum + palace.stars.length, 0);
  const decorativePlacements = palaces.reduce((sum, palace) => sum + palace.decorativeStars.length, 0);
  const labels = palaces.flatMap((palace) => [...palace.stars.map((star) => star.label), ...palace.decorativeStars.map((star) => star.label)]);
  const uniqueStarLabels = new Set(labels).size;
  const totalPlacements = corePlacements + decorativePlacements;
  const complete108 = corePlacements === 66 && decorativePlacements === 48 && totalPlacements === 114 && uniqueStarLabels === 108;
  return Object.freeze({
    corePlacements,
    decorativePlacements,
    totalPlacements,
    uniqueStarLabels,
    expectedUniqueStarLabels: 108 as const,
    complete108,
    disclosure: complete108
      ? '目前正式 iztro@2.6.0 default Ruleset：66 個核心星曜位置 + 48 個十二神位置 = 114 個配置；扣除 6 個跨組同名，為 108 個唯一星名。'
      : '此命盤未達目前正式 108 星完整度 Gate；不會用虛構星曜補足。',
  });
}

function chartStarLabel(chart: NormalizedChart, starId: string | undefined): string {
  if (!starId) return '—';
  for (const palaceId of chart.palaceOrder) {
    const star = [...chart.palaces[palaceId].majorStars, ...chart.palaces[palaceId].minorStars, ...chart.palaces[palaceId].adjectiveStars].find((item) => item.id === starId);
    if (star) return star.label;
  }
  return starId;
}

export function buildProductionChartPageViewModel(chart: NormalizedChart): ProductionChartPageViewModel {
  const palaces = chart.palaceOrder.map((palaceId) => {
    const palace = chart.palaces[palaceId];
    const relations = chart.relations
      .filter((edge) => edge.from === palaceId && edge.to !== palaceId)
      .map((edge) => Object.freeze({
        palaceId: edge.to,
        palaceLabel: chart.palaces[edge.to].label,
        relation: edge.type,
      }))
      .sort((a, b) => a.relation.localeCompare(b.relation) || a.palaceId.localeCompare(b.palaceId));
    return Object.freeze({
      palaceId,
      palaceLabel: palace.label,
      heavenlyStem: palace.heavenlyStem,
      earthlyBranch: palace.earthlyBranch,
      isSoulPalace: chart.soulPalaceId === palaceId,
      isBodyPalace: palace.isBodyPalace,
      stars: chartStars(chart, palaceId),
      decorativeStars: chartDecorativeStars(chart, palaceId),
      relations: Object.freeze(relations),
    });
  });
  if (palaces.length !== 12 || new Set(palaces.map((item) => item.palaceId)).size !== 12) {
    throw new Error('Production chart page requires exactly 12 unique palaces');
  }
  if (palaces.filter((item) => item.isSoulPalace).length !== 1) throw new Error('Production chart page requires exactly one soul palace');
  if (palaces.filter((item) => item.isBodyPalace).length !== 1) throw new Error('Production chart page requires exactly one body palace');
  const starCoverage = buildStarCoverage(palaces);
  const center = Object.freeze({
    zodiac: chart.zodiac,
    fiveElementsClass: chart.fiveElementsClass,
    soulStarLabel: chartStarLabel(chart, chart.soulStarId),
    bodyStarLabel: chartStarLabel(chart, chart.bodyStarId),
  });
  return Object.freeze({
    model: 'PRODUCTION-CHART-PAGE-0.3.0' as const,
    palaceOrder: Object.freeze([...chart.palaceOrder]),
    palaces: Object.freeze(palaces),
    desktopLayout: 'twelve_palace_grid' as const,
    mobileLayout: 'focus_palace' as const,
    defaultFocusPalaceId: chart.soulPalaceId,
    center,
    starCoverage,
    disclosure: '十二宮呈現主星、輔星、雜曜與四組十二神；108 是目前正式 iztro@2.6.0 default 星名 Catalog 的唯一名稱數，不把 114 個 placement 誤稱為 114 顆獨立星曜。',
  });
}
